<script setup lang="ts">
import {ref, onBeforeMount, onMounted, onBeforeUnmount, computed, watch, nextTick} from 'vue';
import dayjs from 'dayjs';
import {useI18n} from 'vue-i18n';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {useLoadingNotification, useLoadingSpinner} from '@/ts/composables/pure/use-loading-notification';
import LoadingError from '@/vue/templates/loading-error.vue';
import type {
  JournalEntryDTO, JournalTemplateDTO, JournalInterventionDTO,
  GetJournalsParams, DayMoodDTO, MoodDTO,
} from '@/ts/types/dto/journals-dto';
import {usePagedCollection} from '@/ts/composables/stateful/use-paged-collection';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import PeriodFilterDropdown from '@/vue/molecules/period-filter-dropdown.vue';
import InterventionFilterDropdown from '@/vue/molecules/intervention-filter-dropdown.vue';
import MoodFilterDropdown from '@/vue/molecules/mood-filter-dropdown.vue';
import {PeriodFilter} from '@/ts/types/component/period-filter';
import {formatDateYMD, getStartOfWeek} from '@/ts/utils/pure-functions';
import {useProfileDateFormat} from '@/ts/composables/stateful/use-profile-date-format';
import JournalEntryModal from '@/vue/molecules/journal-entry-modal.vue';
import JournalEntryDetailModal from '@/vue/molecules/journal-entry-detail-modal.vue';
import JournalEntryCard from '@/vue/molecules/journal-entry-card.vue';
import {useJournalSessionRuns} from '@/ts/composables/pure/use-journal-session-runs';
import JournalEntrySummaryBox from '@/vue/molecules/journal-entry-summary-box.vue';
import MoodMap from '@/vue/molecules/mood-map.vue';

const logger = useLogger('JournalPage');
const api = useApi();
const {t} = useI18n();

const PAGE_SIZE = 10;
const DATE_FORMAT = 'YYYY-MM-DD';

const journalTemplate = ref<JournalTemplateDTO | null>(null);
const isModalOpen = ref(false);
const isDetailModalOpen = ref(false);
const selectedEntry = ref<JournalEntryDTO | null>(null);
const selectedPeriodFilter = ref<PeriodFilter>(PeriodFilter.CURRENT_WEEK);
/**
 * The periods, shortest first, and each one contains the one above it.
 *
 * `LAST_WEEK` and `LAST_MONTH` are trailing windows ending today, not the previous calendar week
 * and month. Calendar periods do not nest: on the 4th of a month "this month" was a four-day
 * window holding fewer entries than "this week", and "last week" was a Mon-Sun block that excluded
 * the current week entirely, so moving down the list could show *less*. Trailing windows mean
 * today's entries are in every option, and each step out only ever adds.
 */
const availablePeriodFilters: PeriodFilter[] = [
  PeriodFilter.TODAY,
  PeriodFilter.CURRENT_WEEK,
  PeriodFilter.LAST_WEEK,
  PeriodFilter.LAST_MONTH,
  PeriodFilter.LAST_YEAR,
  PeriodFilter.ALL,
];

/** Days in each trailing window, counting today as the first. */
const TRAILING_WEEK_DAYS = 7;
const TRAILING_MONTH_DAYS = 30;
const TRAILING_YEAR_DAYS = 365;

const dayMoods = ref<DayMoodDTO[]>([]);

/** Null = every intervention; 'none' = the entries under none. Mirrors the query parameter. */
const selectedInterventionId = ref<string | null>(null);
const selectedMoods = ref<MoodDTO[]>([]);

// The filter's own options, refreshed with each list response.
const availableInterventions = ref<JournalInterventionDTO[]>([]);
const hasStandaloneEntries = ref(false);

/**
 * Whether the intervention filter has anything to narrow.
 *
 * With no intervention in the facet the only options are "All" and "Outside of a program", and
 * every entry is standalone — so both return the same list and the control is a choice between one
 * thing and itself. Hidden rather than disabled: there is nothing for the reader to come back to
 * until they write an entry inside a programme.
 */
const showInterventionFilter = computed(() => availableInterventions.value.length > 0);
let pendingFacet: {interventions: JournalInterventionDTO[]; standalone: boolean} | null = null;
/**
 * Which list request the staged facet belongs to.
 *
 * The paged composable's own request id keeps a stale response off the list, but `pendingFacet`
 * sat outside that: whichever response returned last re-staged its facet, so a slow one could
 * publish the previous period's interventions over the current period's entries — and the
 * auto-clear could then null a selection that the newer facet still offered. Fetches start in
 * order, so the highest token started last and is the only one allowed to stage.
 */
let facetToken = 0;
let stagedFacetToken = -1;

const browserTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
// "This week" starts on the day the reader's country starts its weeks: Sunday in the States.
const datePatterns = useProfileDateFormat();

type DateRange = Pick<GetJournalsParams, 'startDate' | 'endDate'>;

function getDateRangeForFilter(filter: PeriodFilter): DateRange {
  const now = new Date();
  const today = formatDateYMD(now);

  switch (filter) {
    case PeriodFilter.TODAY:
      return {startDate: today, endDate: today};
    case PeriodFilter.CURRENT_WEEK: {
      const weekStart = getStartOfWeek(dayjs(now), datePatterns.value.weekStartsOn);
      return {startDate: weekStart.format(DATE_FORMAT), endDate: today};
    }
    case PeriodFilter.LAST_WEEK:
      return {
        startDate: dayjs(now).subtract(TRAILING_WEEK_DAYS - 1, 'day').format(DATE_FORMAT),
        endDate: today,
      };
    case PeriodFilter.LAST_MONTH:
      return {
        startDate: dayjs(now).subtract(TRAILING_MONTH_DAYS - 1, 'day').format(DATE_FORMAT),
        endDate: today,
      };
    case PeriodFilter.LAST_YEAR:
      return {
        startDate: dayjs(now).subtract(TRAILING_YEAR_DAYS - 1, 'day').format(DATE_FORMAT),
        endDate: today,
      };
    case PeriodFilter.ALL:
    default:
      // The entry list stays genuinely unbounded here; only the map caps its span, since a truly
      // open range would grow a month block per month for as long as the user has been journalling.
      return {};
  }
}

/**
 * The map's own floor for a period that has none: the day of the reader's first recorded mood.
 *
 * It used to stop twelve months back, because every month block was rendered and several years of
 * them was more than a phone should be asked to lay out. The blocks are recycled now, so the span
 * costs what is on screen rather than what exists, and "all entries" can mean all of them on the
 * map as it always has in the list.
 */

/**
 * Which of the mood map's three layouts this period wants. A single day still reads best as its
 * week, so the user can see it in context rather than as one lonely circle.
 */
function getMoodMapMode(filter: PeriodFilter): 'week' | 'span' | 'range' {
  switch (filter) {
    // Trailing windows line up with neither a calendar week nor a month, so they get drawn
    // day-for-day. Padding them out to whole months would colour days the list excludes.
    case PeriodFilter.LAST_WEEK:
    case PeriodFilter.LAST_MONTH:
      return 'span';
    case PeriodFilter.TODAY:
    case PeriodFilter.CURRENT_WEEK:
      return 'week';
    default:
      return 'range';
  }
}

const moodMapMode = computed(() => getMoodMapMode(selectedPeriodFilter.value));

/**
 * The header's wording follows the period, not the layout. "Today" and "Last week" both draw a
 * week, but only one of them is this week.
 */
const moodMapCountKey = computed(() => {
  switch (selectedPeriodFilter.value) {
    case PeriodFilter.TODAY:
    case PeriodFilter.CURRENT_WEEK:
      return 'mood_map_tracked_week';
    case PeriodFilter.LAST_WEEK:
      return 'mood_map_tracked_last_7';
    case PeriodFilter.LAST_MONTH:
      return 'mood_map_tracked_last_30';
    case PeriodFilter.LAST_YEAR:
      return 'mood_map_tracked_last_year';
    default:
      return 'mood_map_tracked_total';
  }
});

const currentDateRange = computed(() => getDateRangeForFilter(selectedPeriodFilter.value));

/**
 * The floor for the periods that put none of their own on the map. Takes the user's earliest
 * recorded mood when that is inside the window — `days` is not promised to be sorted, so this
 * takes the minimum rather than trusting the first element.
 */
const allTimeMapStart = computed(() => {
  const earliest = dayMoods.value.reduce<string | null>(
    (min, day) => (min === null || day.date < min ? day.date : min),
    null,
  );
  // Nothing recorded yet: start at today rather than at the epoch, which would draw a runway of
  // empty months.
  return earliest ?? formatDateYMD(new Date());
});

/**
 * The map draws whole weeks and whole months, so its range is wider than the entry list's: picking
 * "Today" still shows the day in its week, and "This week" shows the days still to come. The fetch
 * below asks for exactly this range, so the map is never handed days it cannot draw, nor asked to
 * grey out days it simply was not told about.
 *
 * With no floor on the period the map covers the last twelve months rather than everything, and
 * starts later when the user's first recorded mood is more recent, so a new account gets a short
 * map instead of a year of blanks.
 */
const mapPeriodStart = computed(() => {
  switch (selectedPeriodFilter.value) {
    case PeriodFilter.TODAY:
    case PeriodFilter.CURRENT_WEEK:
      return getStartOfWeek(dayjs(), datePatterns.value.weekStartsOn).format(DATE_FORMAT);
    // A trailing window is drawn exactly as asked for, so the map's bounds are the list's bounds.
    case PeriodFilter.LAST_WEEK:
    case PeriodFilter.LAST_MONTH:
    case PeriodFilter.LAST_YEAR:
      return currentDateRange.value.startDate ?? formatDateYMD(new Date());
    default:
      return allTimeMapStart.value;
  }
});

const mapPeriodEnd = computed(() => {
  switch (selectedPeriodFilter.value) {
    case PeriodFilter.TODAY:
    case PeriodFilter.CURRENT_WEEK:
      return getStartOfWeek(dayjs(), datePatterns.value.weekStartsOn).add(6, 'day').format(DATE_FORMAT);
    default:
      return currentDateRange.value.endDate ?? formatDateYMD(new Date());
  }
});

/**
 * The entry list, a page at a time as the user scrolls.
 *
 * `usePagedCollection` rather than a "load more" button and a hand-rolled page counter: it brings
 * the sentinel observer and the two failure states with their retries, and those are where every
 * bug on this shape has lived on the pages that grew their own.
 *
 * All three filters go to the backend. Filtering here instead would only ever see the pages already
 * fetched, so a match further down the list would read as "no entries".
 */
const collectionState = usePagedCollection<JournalEntryDTO>({
  key: entry => entry.userComponentId ?? entry.contentId,
  rootSelector: '.global-page-template .content',
  onMissingRoot: () => {
    logger.error('Journal list found no .content scroller; paging falls back to the viewport root')();
  },
  fetchPage: async(page) => {
    facetToken += 1;
    const token = facetToken;
    const dateRange = getDateRangeForFilter(selectedPeriodFilter.value);
    const res = await api.me.getJournals({
      page,
      size: PAGE_SIZE,
      ...dateRange,
      timezone: browserTimezone,
      ...(selectedInterventionId.value && {interventionId: selectedInterventionId.value}),
      ...(selectedMoods.value.length > 0 && {moods: selectedMoods.value}),
    });
    // The facet rides along with the list rather than coming from its own call: it is computed
    // over the same rows, so a separate request could describe a different moment.
    // Defaulted rather than trusted: a backend still on the older contract, or a partial body
    // from a failed response, would otherwise hand the dropdown `undefined` and throw in render.
    if (token >= stagedFacetToken) {
      stagedFacetToken = token;
      pendingFacet = {
        interventions: res.availableInterventions ?? [],
        standalone: res.hasStandaloneEntries ?? false,
      };
    }
    return {items: res.content, page: res.page, totalElements: res.totalElements};
  },
  onError: (err, first) => {
    logger.error(first ? 'Failed to fetch journal entries' : 'Failed to load more journal entries', {err})();
  },
});

const {
  items: journals, totalElements, loadedCount, loading, loadingMore, error, errorMessage, moreError,
  stoppedShort, setSentinel, mountObserver, syncObserver, disconnectObserver,
  loadFirstPage, retryMorePages,
} = collectionState;

const sessionRuns = useJournalSessionRuns(journals);

/**
 * Applies the facet the last settled load carried.
 *
 * `fetchPage` runs before the composable's own request-id guard, so assigning straight from there
 * let a slow response overwrite the dropdown after a newer one had already painted — offering an
 * intervention that holds nothing in the period on screen. Staged in `pendingFacet` and committed
 * only once the awaited load has returned, which is after that guard has had its say.
 */
function commitFacet(): void {
  if (pendingFacet === null) {
    return;
  }
  availableInterventions.value = pendingFacet.interventions;
  hasStandaloneEntries.value = pendingFacet.standalone;
  pendingFacet = null;

  // The control disappears once the facet has no intervention to offer, so a selection made while
  // it was there must not survive it — an invisible filter still narrowing the list is a page the
  // reader cannot argue with.
  if (!showInterventionFilter.value && selectedInterventionId.value !== null) {
    selectedInterventionId.value = null;
  }
}

const [fetchJournalTemplate] = useLoadingNotification(
  async() => {
    journalTemplate.value = await api.me.getJournalTemplate();
    logger.debug('Journal template fetched', {template: journalTemplate.value})();
  },
);

/**
 * What the map asks the server for. Deliberately the map's own range, not the entry list's: the
 * list stops at today, the map draws whole weeks and months, and asking for the narrower one would
 * render real moods as untracked days.
 *
 * Kept independent of `mapPeriodStart` for the unbounded period, which narrows itself to the
 * earliest mood in the response — deriving the request from that would be circular.
 */
const moodFetchRange = computed<DateRange>(() => {
  if (currentDateRange.value.startDate) {
    return {startDate: mapPeriodStart.value, endDate: mapPeriodEnd.value};
  }
  // No start date at all: the endpoint takes an open lower bound, and the query behind it now
  // selects two columns per row rather than whole entries, so the cost is a day's worth of history
  // rather than everything the reader ever wrote.
  return {endDate: mapPeriodEnd.value};
});

/** The range the map is currently drawing, so a fetch can tell whether it has anything to do. */
let shownRange: string | null = null;

function rangeKey(range: DateRange): string {
  return `${range.startDate ?? ''}..${range.endDate ?? ''}`;
}

const [runDayMoodsFetch] = useLoadingNotification(
  async() => {
    const range = moodFetchRange.value;
    const res = await api.me.getDayMoods({...range, timezone: browserTimezone});
    dayMoods.value = res.days;
    shownRange = rangeKey(range);
    logger.debug('Day moods fetched', {days: res.days.length})();
  },
);

/**
 * The map's fetches run one after another rather than concurrently.
 *
 * `useLoadingNotification` *drops* a call made while one is in flight. Two period changes inside
 * one request's flight time — an ordinary gesture on a slow connection — therefore lost the second
 * one entirely, with nothing to re-fetch later: the map kept the first period's days and drew the
 * second period's dates over them, showing recorded moods as days that were never logged.
 *
 * Chained on the previous call, each fetch waits its turn and then asks whether the range it wants
 * is already the one on screen. So no call is discarded, the newest range always wins, and the
 * composable's own guard never fires. Errors leave `shownRange` untouched, so the next change
 * retries rather than trusting a period that failed to load.
 */
let moodFetchChain: Promise<void> = Promise.resolve();

async function fetchDayMoods({force = false}: {force?: boolean} = {}): Promise<void> {
  moodFetchChain = moodFetchChain.then(async() => {
    // `force` is for a change to the days themselves rather than to the range around them. Saving
    // an entry moves no bound, so the range test alone read it as "already on screen" and the mood
    // just recorded did not appear until the reader changed period — the guard against re-fetching
    // an unchanged range swallowing the one fetch that had new data to bring back.
    if (force || rangeKey(moodFetchRange.value) !== shownRange) {
      await runDayMoodsFetch();
    }
  });
  // Awaited, not returned: the caller waits for this fetch's turn, and the chain keeps its own
  // reference so the next call queues behind it either way.
  await moodFetchChain;
}

/**
 * Any filter change reloads the list from page 0 and re-arms the observer.
 *
 * The map only re-fetches for the period: it is not narrowed by the other two, it dims instead, so
 * asking the server again for the same days would change nothing on screen.
 */
watch(selectedPeriodFilter, async() => {
  logger.debug('Period changed, re-fetching', {filter: selectedPeriodFilter.value})();
  // A mood or intervention chosen in one period may hold nothing in the next; the facet arrives
  // with the reload, and a stale selection would show as a filter the dropdown no longer offers.
  //
  // Guarded, because assigning a fresh `[]` counts as a change even when nothing was selected, and
  // the watcher below would then fire a second, identical page-0 request on every period change.
  if (selectedInterventionId.value !== null) {
    selectedInterventionId.value = null;
  }
  if (selectedMoods.value.length > 0) {
    selectedMoods.value = [];
  }
  void fetchDayMoods();
  await loadFirstPage();
  commitFacet();
});

watch([selectedInterventionId, selectedMoods], async() => {
  logger.debug('Entry filter changed, re-fetching', {
    interventionId: selectedInterventionId.value,
    moods: selectedMoods.value,
  })();
  await loadFirstPage();
  commitFacet();
});

function openCreateModal(): void {
  isModalOpen.value = true;
}

function handleEntryClick(entry: JournalEntryDTO): void {
  selectedEntry.value = entry;
  isDetailModalOpen.value = true;
}

/**
 * How many of one day's entries to ask for. A day's worth rather than a page's: the answer is
 * scanned for the one the circle stands for, so a reader who wrote a dozen times still gets it.
 *
 * Also the most the endpoint will give — its `size` is capped at 50 — so this cannot be raised
 * here alone.
 */
const DAY_ENTRIES_SIZE = 50;

/**
 * Fetches one day and opens the entry its circle stands for.
 *
 * The map holds a date and a mood, never an entry, so the entry has to be asked for. It is fetched
 * rather than looked up in the loaded list because the list is paged: a circle from two years back
 * is usually not in it, and a click that works only on this month's days is worse than one that
 * always works.
 *
 * The circle stands for the newest mood recorded that day, so that is the entry opened — a day
 * written in three times opens the one whose colour was clicked, not its first of the morning.
 * Both the list and the map's own data are ordered `updateDate desc` by the backend, which is what
 * makes the first entry carrying a mood the same one the circle was coloured from.
 *
 * `useLoadingSpinner` rather than the bare notification, so the wait is visible: a tap on a slow
 * connection was otherwise a circle that did nothing for a second.
 */
const [runDayOpen] = useLoadingSpinner(async(date: string) => {
  const res = await api.me.getJournals({
    startDate: date,
    endDate: date,
    timezone: browserTimezone,
    size: DAY_ENTRIES_SIZE,
  });
  const entry = res.content.find(candidate => candidate.mood) ?? res.content[0];
  if (entry) {
    handleEntryClick(entry);
    return;
  }
  // A circle the list cannot account for. Both come from the same entries under the same consent
  // rules, so this is not expected — and there is nothing to open if it happens.
  logger.warn('Mood map day had no entry behind it', {date})();
});

/** The day most recently asked for, so a queued fetch can tell it has been overtaken. */
let requestedDay: string | null = null;
let dayOpenChain: Promise<void> = Promise.resolve();

/**
 * Opening a day from the mood map, one at a time.
 *
 * Chained for the same reason the mood fetch is: the composable underneath *drops* a call made
 * while another is in flight, so two taps inside one request's flight time lost the second one
 * silently — no modal, no error, nothing to retry. Queued instead, and a day that has been
 * overtaken by a later tap is skipped rather than opening a modal the reader has moved on from.
 */
async function openDayFromMap(date: string): Promise<void> {
  requestedDay = date;
  dayOpenChain = dayOpenChain.then(async() => {
    if (requestedDay === date) {
      await runDayOpen(date);
    }
  });
  await dayOpenChain;
}

/**
 * Reload rather than splice the new entry in. It has to pass the same filters as everything else,
 * and the page cannot tell on its own whether it does: an entry saved while "Last week" or a mood
 * the user did not pick is showing would otherwise appear in a list that is supposed to exclude it.
 */
async function handleJournalSaved(newJournal: JournalEntryDTO): Promise<void> {
  logger.debug('Journal entry saved, reloading the list', {newJournal})();
  if (newJournal.mood) {
    // Forced: the period has not moved, but one of its days now carries a mood it did not.
    void fetchDayMoods({force: true});
  }
  await loadFirstPage();
  // The reload stages a fresh facet; without committing it, writing a first standalone entry left
  // "Outside of a program" unoffered until the next filter change.
  commitFacet();
}

/**
 * Whether the page has ever finished a first load.
 *
 * `loading-error` renders its default slot behind a `v-else`, so while `loading` is true the whole
 * page — filters, map and list — is unmounted. That is right for the first load and wrong for every
 * refetch after it: a filter change would blank the page the user is filtering, and an open
 * dropdown would be torn out from under them mid-selection. So the blocking state covers the first
 * load only, and later refetches swap the list in place.
 */
const hasLoadedOnce = ref(false);
const isFirstLoad = computed(() => loading.value && !hasLoadedOnce.value);

/** "N of M entries shown" — built here because the nested options break the template parser. */
const loadedStatusLabel = computed(() => t(
  'journal_loaded_status',
  totalElements.value,
  {named: {loaded: loadedCount.value, count: totalElements.value}},
));

let unmounted = false;

onBeforeMount(() => {
  void fetchJournalTemplate();
  void fetchDayMoods();
});

onMounted(async() => {
  await loadFirstPage();
  commitFacet();
  hasLoadedOnce.value = true;
  // The page can be gone by the time the first page lands, and the observer below would then
  // outlive onBeforeUnmount.
  if (unmounted) {
    return;
  }
  mountObserver();
  await nextTick();
  syncObserver();
});

onBeforeUnmount(() => {
  unmounted = true;
  disconnectObserver();
});
</script>

<template>
  <div class="journal-page">
    <!-- Header -->
    <div class="journal-header">
      <h1 class="title">
        {{ $t('journal') }}
      </h1>
      <svg-illustration class="arrow-right" name="right-down-arrow.svg" inner-class="inner-right"/>
    </div>

    <!-- Journals History -->
    <loading-error id="journal-page" :loading="isFirstLoad" :error="error ? errorMessage : null" type="page-ignore-header">
      <div class="journal-history-card">
        <div class="history-header">
          <h2 class="history-title">
            {{ $t('your_entries') }}
          </h2>
          <balm-btn
            v-if="journalTemplate"
            theme="grey"
            :label="$t('create_entry')"
            icon="plus.svg"
            icon-position="left"
            paddings="p2-5"
            @click="openCreateModal"
          />
        </div>
        <!-- The three filters read as one row: what it belongs to, when, and how it felt. -->
        <div class="filter-row">
          <intervention-filter-dropdown
            v-if="showInterventionFilter"
            v-model="selectedInterventionId"
            :available="availableInterventions"
            :has-standalone="hasStandaloneEntries"
          />
          <period-filter-dropdown
            v-model="selectedPeriodFilter"
            :available-filters="availablePeriodFilters"
          />
          <mood-filter-dropdown v-model="selectedMoods"/>
        </div>
        <div class="summary-boxes">
          <journal-entry-summary-box
            :count="totalElements"
            :subtitle="$t('journal_entries')"
            selected
            inactive
          />
        </div>
        <mood-map
          :days="dayMoods"
          :mode="moodMapMode"
          :count-label-key="moodMapCountKey"
          :period-start="mapPeriodStart"
          :period-end="mapPeriodEnd"
          :highlight-moods="selectedMoods"
          @day-select="openDayFromMap"
        />
        <div v-if="journals.length > 0" class="entries-list">
          <!-- The run is a container of its own so the name above it stops at the entries it
               belongs to, for a reader and for a screen reader both. A group rather than a
               region: a session filled over two sittings is named twice by design, and two
               landmarks with one name is worse than none. -->
          <div
            v-for="run in sessionRuns"
            :key="run.key"
            class="session-run"
            :role="run.heading ? 'group' : undefined"
            :aria-labelledby="run.heading ? `session-run-${run.key}` : undefined"
          >
            <h3 v-if="run.heading" :id="`session-run-${run.key}`" class="session-heading">
              {{ run.heading }}
            </h3>
            <journal-entry-card
              v-for="entry in run.entries"
              :key="entry.userComponentId ?? entry.contentId"
              :entry="entry"
              @click="handleEntryClick(entry)"
            />
          </div>
        </div>
        <!--
          The refetch state comes before the empty one. `loadFirst` clears the list synchronously,
          so a filter change would otherwise flash "no entries" and a count of zero over a period
          that has plenty — the page saying the opposite of what it is about to show.
        -->
        <div v-else-if="loading" class="journal-loading">
          <q-spinner-dots color="primary" size="32px"/>
        </div>
        <div v-else class="empty-state">
          <p>{{ $t('no_journal_entries') }}</p>
        </div>

        <!-- The visible list is what it is; this region is what announces that it grew. -->
        <p class="sr-only" role="status">
          {{ loadedStatusLabel }}
        </p>

        <!-- Watched rather than scroll-listened: fires once per crossing, and needs no knowledge
             of which ancestor scrolls. -->
        <div :ref="(el) => setSentinel(el as HTMLElement | null)" class="journal-sentinel" aria-hidden="true"/>

        <div v-if="loadingMore" class="journal-loading">
          <q-spinner-dots color="primary" size="32px"/>
        </div>

        <!-- A page that fails mid-scroll keeps the entries already loaded and offers a retry,
             rather than replacing the whole list with an error. `stoppedShort` is the same
             affordance for a walk that ended before reaching the total. -->
        <div v-else-if="moreError || stoppedShort" class="journal-more-error" role="alert">
          <p class="journal-more-error__text">
            {{ $t('explore_filter_error_helper') }}
          </p>
          <button type="button" class="journal-more-error__retry" @click="retryMorePages">
            {{ $t('explore_filter_retry') }}
          </button>
        </div>
      </div>
    </loading-error>

    <!-- Detail Modal -->
    <journal-entry-detail-modal
      v-model="isDetailModalOpen"
      :entry="selectedEntry"
    />

    <!-- Create Entry Modal -->
    <journal-entry-modal
      v-if="journalTemplate"
      v-model="isModalOpen"
      :template="journalTemplate"
      @save="handleJournalSaved"
    />
  </div>
</template>

<style scoped lang="scss">
.journal-page {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
  padding: var(--Spacing-spacing-5);
}

.journal-header {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-2);
}

.title {
  @include font-h1-bold;
}

.journal-history-card {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
  padding: var(--Spacing-spacing-4);
  background: var(--Neutral-UI-neutral-ui-0);
  border-radius: var(--card-border-radius);
  width: 100%;

  @include screen-md {
    padding: var(--Spacing-spacing-6);
  }
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--Spacing-spacing-3);
  flex-wrap: wrap;
}

.history-title {
  @include font-display-3;
  color: var(--Neutral-UI-neutral-ui-100);
  margin: 0;
}

.summary-boxes {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--Spacing-spacing-3);

  @include screen-md {
    grid-template-columns: repeat(3, 1fr);
  }
}

.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--Spacing-spacing-2);
  align-self: flex-start;
}

// Twice the gap a run keeps between its own cards, so a name is read as belonging to the entries
// under it and not to the ones a run boundary has already ended. Entries under no session are one
// run between them all, so a journal with no session in it never sees this gap at all and looks
// exactly as it did before there were runs.
.entries-list {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-5);
}

.session-run {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-3);
}

.session-heading {
  @include font-body-bold;
  color: var(--Neutral-UI-neutral-ui-80);
  margin: 0;
}

.journal-sentinel {
  // No height of its own: it only has to cross the scroller's edge to fire the observer.
  height: 1px;
}

.journal-loading {
  display: flex;
  justify-content: center;
  padding: var(--Spacing-spacing-5) 0;
}

.journal-more-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-3);
  padding: var(--Spacing-spacing-5) 0;
}

.journal-more-error__text {
  @include font-body-sm;
  color: var(--Neutral-UI-neutral-ui-60);
  margin: 0;
}

.journal-more-error__retry {
  @include font-body-sm-bold;
  @include focus-visible;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  color: var(--Neutral-UI-neutral-ui-100);
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--Spacing-spacing-8);
  text-align: center;

  p {
    @include font-body;
    color: var(--Neutral-UI-neutral-ui-60);
  }
}
</style>
